import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tag } from "@scribenote/brian-design-system";
import type { Task } from "@tasks/tasksClient";
import { useTaskAssignee } from "@tasks/useTaskAssignee";

/**
 * Who owns a task: the assigned user, or the assigned role when no user is set
 * (WEB-3198). A tag, not a highlighter - an owner is a fact about the task, not
 * a state it is in, so it takes the hairline tag and never a wash (WEB-3328).
 *
 * Users and roles must read differently at a glance, so "Sarah" isn't mistaken
 * for "Front desk", and the glyph carries that: one person vs a group. Colour
 * carried it before; a tag has no colour to spend, and the icon says the same
 * thing without borrowing a status hue that means something else here.
 *
 * Name resolution is best-effort from the clinic users/roles lists; until they
 * load (or if the task is unassigned) nothing renders.
 *
 * Worn by every surface outside the migrated /tasks route (the subject pane, the
 * notifications feed); `TaskIndexCard` writes its owner straight
 * onto the filing line instead, since a card's own ruling is its tag.
 */
export const TaskAssigneeBadge = ({
  task,
  className,
}: {
  task: Task;
  className?: string;
}) => {
  const assignee = useTaskAssignee(task);

  if (!assignee) return null;

  return (
    <div className={className}>
      <Tag
        icon={<FontAwesomeIcon icon={assignee.icon} />}
        data-testid={`task-assignee-${task.id}`}
      >
        {assignee.name}
      </Tag>
    </div>
  );
};
